import { Link } from "@tanstack/react-router";
import { Cpu, FlaskConical, KeyRound, RadioTower, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useDevices } from "@/api/hooks";
import type { Device, DeviceReading, GeneratorTelemetry, TankTelemetry } from "@/api/types";
import { PageHeader } from "@/components/app-shell";
import { Badge, Button, Card, CardBody, MicroLabel, Num } from "@/components/ui/primitives";
import { EmptyState, ErrorState, LoadingRows } from "@/components/ui/states";
import type { TKey } from "@/i18n/dict";
import { useI18n } from "@/i18n/provider";
import { formatNumber, formatRelativeSeconds } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Field devices — the ESP32 generator-monitor boards.
 *
 * A board is not created here. It REGISTERS ITSELF the first time it reports with one of the
 * organization's ingest keys, so the list is a window, not a form, and its empty state has
 * to explain the one thing a customer needs to do to fill it.
 *
 * `online` comes from the server (synced within 30 s), never recomputed here: a laptop with
 * a wrong clock must not declare a live generator dead.
 */

/* ------------------------------------------------------------- shared bits */

/** Re-render on a clock, so "8 seconds ago" keeps counting between polls. */
export function useNow(intervalMs = 1_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function deviceTitle(device: Pick<Device, "name" | "hardware_id">): string {
  return device.name ?? device.hardware_id;
}

export function OnlineBadge({ online }: { online: boolean }) {
  const { t } = useI18n();
  return (
    <Badge tone={online ? "ok" : "idle"}>
      <span
        aria-hidden
        className={cn("size-1.5 rounded-full", online ? "bg-ok" : "bg-mauve")}
      />
      {t(online ? "devices.online" : "devices.offline")}
    </Badge>
  );
}

/* ------------------------------------------------------------ data source */

/**
 * WHERE A NUMBER CAME FROM — the first thing a reading has to say about itself.
 *
 * There are three different claims here and a customer must never confuse them:
 *   - `simulated`          the firmware invented these values. Not the generator.
 *   - `live`               the board read them off the generator's own controller.
 *   - `controller-offline` the board is fine and talking to us, but the controller is
 *                          not answering IT. There are no generator values at all, and
 *                          the screen must say that rather than show the last ones as
 *                          though they were current.
 * `unknown` is a board that predates the `controller_online` flag: real data, no claim.
 */
export type DataSource = "simulated" | "live" | "controller-offline" | "unknown";

export function dataSourceOf(reading: DeviceReading | undefined): DataSource {
  if (!reading) return "unknown";
  if (reading.dummy) return "simulated";
  if (reading.controller_online === false) return "controller-offline";
  if (reading.controller_online === true) return "live";
  return "unknown";
}

/**
 * The generator values a screen is ALLOWED to show. A controller that did not answer has
 * none — whatever the row still carries is stale by definition, and rendering it would be
 * the one mistake this whole flag exists to prevent.
 */
export function shownGenerator(reading: DeviceReading | undefined): GeneratorTelemetry {
  if (!reading || dataSourceOf(reading) === "controller-offline") return {};
  return reading.generator ?? {};
}

/**
 * Simulated values must never be mistaken for the generator. The firmware says so with
 * `dummy`, and this badge carries that fact onto every surface a reading appears on.
 */
export function SimulatedBadge() {
  const { t } = useI18n();
  return (
    <Badge tone="ink" title={t("devices.simulatedHint")}>
      <FlaskConical className="size-3" aria-hidden />
      {t("devices.simulated")}
    </Badge>
  );
}

/** The opposite claim, and the one a customer wants: these came off the controller. */
export function LiveBadge() {
  const { t } = useI18n();
  return (
    <Badge tone="ok" title={t("devices.liveHint")}>
      <RadioTower className="size-3" aria-hidden />
      {t("devices.live")}
    </Badge>
  );
}

/** Board alive, controller silent — a fault to act on, not a quiet absence of data. */
export function ControllerOfflineBadge() {
  const { t } = useI18n();
  return (
    <Badge tone="dry" title={t("devices.controllerOfflineHint")}>
      <TriangleAlert className="size-3" aria-hidden />
      {t("devices.controllerOffline")}
    </Badge>
  );
}

export function DataSourceBadge({ source }: { source: DataSource }) {
  if (source === "simulated") return <SimulatedBadge />;
  if (source === "live") return <LiveBadge />;
  if (source === "controller-offline") return <ControllerOfflineBadge />;
  return null;
}

/* ----------------------------------------------------------- engine state */

export type EngineState = NonNullable<GeneratorTelemetry["engine_state"]>;

/**
 * What the engine is doing. `engine_state` is the controller's own word for it; a controller
 * that does not expose one still tells us whether it is running, and that is a truthful, if
 * coarser, answer. Only a controller that says neither leaves this undefined.
 */
export function engineStateOf(generator: GeneratorTelemetry | undefined): EngineState | undefined {
  if (!generator) return undefined;
  if (generator.engine_state) return generator.engine_state;
  if (generator.running === undefined) return undefined;
  return generator.running ? "running" : "stopped";
}

const ENGINE_TONE: Record<EngineState, "ok" | "low" | "dry" | "idle"> = {
  running: "ok",
  starting: "low",
  stopping: "low",
  error: "dry",
  stopped: "idle",
};

/**
 * The chip an operator looks for first. The dot takes the badge's own colour
 * (`bg-current`), so the state is legible before the word is read.
 */
export function EngineChip({ state }: { state: EngineState | undefined }) {
  const { t } = useI18n();
  if (!state) {
    return (
      <span className="text-faint" title={t("devices.notProvided")}>
        {t("common.none")}
      </span>
    );
  }
  return (
    <Badge tone={ENGINE_TONE[state]}>
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {t(`devices.engineState.${state}` as const)}
    </Badge>
  );
}

/** "Auto", "Test on load" — the controller's selector switch, in the reader's language. */
export function ControlModeLabel({ mode }: { mode: GeneratorTelemetry["control_mode"] }) {
  const { t } = useI18n();
  if (!mode) {
    return (
      <span className="text-faint" title={t("devices.notProvided")}>
        {t("common.none")}
      </span>
    );
  }
  return <span className="text-sm text-text">{t(`devices.controlMode.${mode}` as const)}</span>;
}

export function LastSeen({ iso, now }: { iso: string; now: number }) {
  const { t, lang } = useI18n();
  const seconds = (now - new Date(iso).getTime()) / 1000;
  if (Number.isNaN(seconds)) return <span>{t("devices.neverSeen")}</span>;
  return <span>{t("devices.lastSeen", { when: formatRelativeSeconds(lang, seconds) })}</span>;
}

type Unit = { key: TKey } | { symbol: "%" | "°C" };

/**
 * Only the fields that carry a FIGURE. `running`, `engine_state` and `control_mode` are
 * states, not measurements — they have their own chips, and typing them out of this table
 * is what stops a string being handed to a number formatter.
 */
type NumericGeneratorField = {
  [K in keyof GeneratorTelemetry]-?: NonNullable<GeneratorTelemetry[K]> extends number ? K : never;
}[keyof GeneratorTelemetry];

export interface MetricDef<F extends string> {
  field: F;
  label: TKey;
  /** Absent for a pure count: "Starts" is 504, not 504 of anything. */
  unit?: Unit;
  digits: number;
  /** Run hours read as "443 h 23 m" — nobody converts 0.386 h in their head. */
  format?: "runHours";
}

export const GENERATOR_METRICS: MetricDef<NumericGeneratorField>[] = [
  { field: "voltage_v", label: "devices.metric.voltage", unit: { key: "devices.unit.volt" }, digits: 1 },
  { field: "current_a", label: "devices.metric.current", unit: { key: "devices.unit.amp" }, digits: 1 },
  { field: "frequency_hz", label: "devices.metric.frequency", unit: { key: "devices.unit.hertz" }, digits: 2 },
  { field: "power_kw", label: "devices.metric.power", unit: { key: "devices.unit.kw" }, digits: 2 },
  { field: "load_pct", label: "devices.metric.load", unit: { symbol: "%" }, digits: 1 },
  { field: "engine_rpm", label: "devices.metric.rpm", unit: { key: "devices.unit.rpm" }, digits: 0 },
  { field: "fuel_pct", label: "devices.metric.fuel", unit: { symbol: "%" }, digits: 1 },
  { field: "oil_pressure_bar", label: "devices.metric.oilPressure", unit: { key: "devices.unit.bar" }, digits: 2 },
  { field: "oil_temp_c", label: "devices.metric.oilTemp", unit: { symbol: "°C" }, digits: 1 },
  { field: "coolant_temp_c", label: "devices.metric.coolant", unit: { symbol: "°C" }, digits: 1 },
  { field: "battery_v", label: "devices.metric.battery", unit: { key: "devices.unit.volt" }, digits: 2 },
  { field: "charge_alt_v", label: "devices.metric.chargeAlt", unit: { key: "devices.unit.volt" }, digits: 1 },
  { field: "run_hours", label: "devices.metric.runHours", digits: 1, format: "runHours" },
  { field: "energy_kwh", label: "devices.metric.energy", unit: { key: "devices.unit.kwh" }, digits: 1 },
  { field: "starts", label: "devices.metric.starts", digits: 0 },
];

export const TANK_METRICS: MetricDef<keyof TankTelemetry>[] = [
  { field: "level_pct", label: "devices.metric.tankLevel", unit: { symbol: "%" }, digits: 1 },
  { field: "level_mm", label: "devices.metric.tankHeight", unit: { key: "devices.unit.mm" }, digits: 0 },
  { field: "volume_l", label: "devices.metric.tankVolume", unit: { key: "common.liters" }, digits: 0 },
  { field: "temperature_c", label: "devices.metric.tankTemperature", unit: { symbol: "°C" }, digits: 1 },
];

/**
 * A figure and its unit.
 *
 * ABSENT AND ZERO ARE NOT THE SAME THING, and this is the one place that distinction is
 * made. A controller that exposes no oil-temperature sensor sends nothing, and that is a
 * dash with a hint saying so. A stopped generator genuinely reads 0 V, 0 Hz, 0 A — printing
 * a dash there would hide a working measurement behind "no data".
 */
export function MetricValue({
  value,
  metric,
  className,
}: {
  value: number | undefined;
  metric: MetricDef<string>;
  className?: string;
}) {
  const { t, lang } = useI18n();

  if (value === undefined) {
    return (
      <span className={cn("text-faint", className)} title={t("devices.notProvided")}>
        {t("common.none")}
      </span>
    );
  }

  if (metric.format === "runHours") {
    const minutes = Math.max(0, Math.round(value * 60));
    return (
      <span className={cn("font-mono text-text", className)}>
        <Num>
          {formatNumber(lang, Math.floor(minutes / 60))} {t("devices.unit.hourShort")}{" "}
          {formatNumber(lang, minutes % 60)} {t("devices.unit.minuteShort")}
        </Num>
      </span>
    );
  }

  const unit = metric.unit ? ("key" in metric.unit ? t(metric.unit.key) : metric.unit.symbol) : null;
  return (
    <span className={cn("font-mono text-text", className)}>
      <Num>{formatNumber(lang, value, metric.digits)}</Num>
      {unit ? <span className="ms-1 text-xs text-faint">{unit}</span> : null}
    </span>
  );
}

/* -------------------------------------------------------------------- list */

const HEADLINE_FIELDS = ["voltage_v", "power_kw", "frequency_hz", "fuel_pct"] as const;

function DeviceCard({ device, now }: { device: Device; now: number }) {
  const { t } = useI18n();
  const reading = device.latest_reading;
  const source = dataSourceOf(reading);
  const generator = shownGenerator(reading);
  const engine = engineStateOf(generator);
  const headline = GENERATOR_METRICS.filter((m) =>
    (HEADLINE_FIELDS as readonly string[]).includes(m.field),
  );

  return (
    <Link
      to="/devices/$deviceId"
      params={{ deviceId: device.id }}
      data-testid={`device-card-${device.id}`}
      className="group block rounded-[6px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cream"
    >
      <Card className="h-full transition-colors group-hover:border-rule-strong">
        <CardBody className="flex h-full flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-text">{deviceTitle(device)}</p>
              {device.name ? (
                <p dir="ltr" className="truncate text-start font-mono text-[11px] text-faint">
                  {device.hardware_id}
                </p>
              ) : null}
            </div>
            <OnlineBadge online={device.online} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {engine ? <EngineChip state={engine} /> : null}
            <DataSourceBadge source={source} />
          </div>

          {reading ? (
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
              {headline.map((metric) => (
                <div key={metric.field} className="min-w-0">
                  <dt>
                    <MicroLabel>{t(metric.label)}</MicroLabel>
                  </dt>
                  <dd>
                    {/* `generator` is empty when the controller went quiet, so every
                        headline falls to a dash instead of quoting a stale bus read. */}
                    <MetricValue value={generator[metric.field]} metric={metric} />
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-xs text-faint">{t("devices.noReading")}</p>
          )}

          <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-rule pt-2 text-[11px] text-faint">
            <LastSeen iso={device.last_seen_at} now={now} />
            {device.fw_version ? (
              <span>
                {t("devices.firmware")} <Num>v{device.fw_version}</Num>
              </span>
            ) : null}
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}

export function DevicesPage() {
  const { t } = useI18n();
  const devicesQuery = useDevices();
  const now = useNow();

  const devices = devicesQuery.data?.data ?? [];

  return (
    <>
      <PageHeader title={t("devices.title")} description={t("devices.subtitle")} />

      {devicesQuery.isPending ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Card key={i}>
              <LoadingRows rows={2} />
            </Card>
          ))}
        </div>
      ) : devicesQuery.error ? (
        <Card>
          <ErrorState error={devicesQuery.error} onRetry={() => void devicesQuery.refetch()} />
        </Card>
      ) : devices.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Cpu className="size-6" />}
            title={t("devices.emptyTitle")}
            body={t("devices.emptyBody")}
            action={
              <Button asChild variant="secondary">
                <Link to="/settings">
                  <KeyRound className="size-4" aria-hidden />
                  {t("devices.emptyAction")}
                </Link>
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {devices.map((device) => (
            <DeviceCard key={device.id} device={device} now={now} />
          ))}
        </div>
      )}
    </>
  );
}
